// src/components/VisitorContactWidget/hooks/useVisitorContactWidget.js

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import {
  addVisitorPageView,
  registerVisitorPopupEvent,
  startVisitorSession,
} from "../../../services/visitorTrackingServices";

import {
  getPublicVisitorConversation,
  markConversationAsReadByVisitor,
  openVisitorConversation,
  sendVisitorMessage,
} from "../../../services/visitorConversationServices";

import {
  createVisitorCallbackRequest,
} from "../../../services/visitorCallbackServices";

import {
  getVisitorSessionToken,
  saveVisitorSessionToken,
} from "../../../analytics/utils/visitorStorage";

import {
  buildVisitorSessionPayload,
} from "../../../analytics/utils/visitorClientUtils";

import {
  CONTACT_WIDGET_VIEWS,
  detectProductFromPath,
} from "../data/contactWidgetData";

import {
  dismissInvitationForSession,
  getStoredConversationId,
  getStoredVisitorContact,
  hasDismissedInvitation,
  saveConversationId,
  saveVisitorContact,
} from "../utils/contactWidgetStorage";

const CONVERSATION_POLL_MS = 5000;
const INVITATION_DELAY_MS = 45000;

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export default function useVisitorContactWidget() {
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const [view, setView] = useState(
    CONTACT_WIDGET_VIEWS.HOME,
  );

  const [conversationId, setConversationId] =
    useState(() => getStoredConversationId());

  const [conversation, setConversation] =
    useState(null);

  const [messages, setMessages] = useState([]);

  const [invitationVisible, setInvitationVisible] =
    useState(false);

  const [loadingConversation, setLoadingConversation] =
    useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const [successData, setSuccessData] =
    useState(null);

  const pollRunningRef = useRef(false);

  const storedContact = useMemo(
    () => getStoredVisitorContact() || {},
    [],
  );

  const suggestedProduct = useMemo(() => {
    return detectProductFromPath(location.pathname);
  }, [location.pathname]);

  const currentPage = useMemo(() => {
    return `${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  /**
   * Récupère le token créé par VisitorTracker.
   * Si le tracker n'a pas encore terminé, une session est créée ici.
   */
  const ensureSessionToken = useCallback(async () => {
    let sessionToken = getVisitorSessionToken();

    if (sessionToken) {
      return sessionToken;
    }

    const response = await startVisitorSession(
      buildVisitorSessionPayload({
        page: currentPage,
        title: document.title,
        search: location.search,
      }),
    );

    sessionToken =
      response?.data?.session_token || null;

    if (!sessionToken) {
      throw new Error(
        "Impossible d'initialiser la session visiteur.",
      );
    }

    saveVisitorSessionToken(sessionToken);

    return sessionToken;
  }, [currentPage, location.search]);

  const registerPopupAction = useCallback(
    async (action) => {
      try {
        const sessionToken =
          await ensureSessionToken();

        await registerVisitorPopupEvent({
          session_token: sessionToken,
          action,
        });
      } catch {
        // Une erreur de tracking ne doit jamais bloquer le widget.
      }
    },
    [ensureSessionToken],
  );

  const openWidget = useCallback(
    (nextView = CONTACT_WIDGET_VIEWS.HOME) => {
      setInvitationVisible(false);
      setIsOpen(true);
      setErrorMessage("");

      if (
        nextView === CONTACT_WIDGET_VIEWS.CHAT &&
        !conversationId
      ) {
        setView(CONTACT_WIDGET_VIEWS.QUESTION);
      } else {
        setView(nextView);
      }

      registerPopupAction("interacted");
    },
    [conversationId, registerPopupAction],
  );

  const closeWidget = useCallback(() => {
    setIsOpen(false);
    setErrorMessage("");
  }, []);

  const goHome = useCallback(() => {
    setErrorMessage("");
    setSuccessData(null);
    setView(CONTACT_WIDGET_VIEWS.HOME);
  }, []);

  const dismissInvitation = useCallback(() => {
    setInvitationVisible(false);
    dismissInvitationForSession();
  }, []);

  const loadConversation = useCallback(
    async ({ silent = false } = {}) => {
      if (!conversationId || pollRunningRef.current) {
        return null;
      }

      pollRunningRef.current = true;

      try {
        if (!silent) {
          setLoadingConversation(true);
        }

        const sessionToken =
          await ensureSessionToken();

        const response =
          await getPublicVisitorConversation(
            conversationId,
            sessionToken,
          );

        const nextConversation =
          response?.data || null;

        setConversation(nextConversation);
        setMessages(
          nextConversation?.messages || [],
        );

        if (
          isOpen &&
          view === CONTACT_WIDGET_VIEWS.CHAT &&
          Number(
            nextConversation?.unread_visitor_count,
          ) > 0
        ) {
          await markConversationAsReadByVisitor(
            conversationId,
            sessionToken,
          );

          setConversation((current) =>
            current
              ? {
                  ...current,
                  unread_visitor_count: 0,
                }
              : current,
          );
        }

        return nextConversation;
      } catch (error) {
        if (!silent) {
          setErrorMessage(
            getErrorMessage(
              error,
              "Impossible de récupérer la conversation.",
            ),
          );
        }

        return null;
      } finally {
        pollRunningRef.current = false;

        if (!silent) {
          setLoadingConversation(false);
        }
      }
    },
    [
      conversationId,
      ensureSessionToken,
      isOpen,
      view,
    ],
  );

  const startQuestionConversation = useCallback(
    async (formData) => {
      try {
        setSubmitting(true);
        setErrorMessage("");

        const sessionToken =
          await ensureSessionToken();

        saveVisitorContact(formData);

        const response =
          await openVisitorConversation({
            session_token: sessionToken,

            visitor_name: formData.name,
            visitor_email: formData.email,
            visitor_phone:
              formData.phone || null,

            organization_name:
              formData.organization_name || null,

            subject:
              formData.subject ||
              "Question depuis le site MonAppliClub",

            initial_message: formData.message,
          });

        const nextConversation =
          response?.data?.conversation;

        const initialMessage =
          response?.data?.message;

        if (!nextConversation?.id) {
          throw new Error(
            "La conversation n'a pas pu être créée.",
          );
        }

        saveConversationId(nextConversation.id);

        setConversationId(nextConversation.id);
        setConversation(nextConversation);

        setMessages(
          initialMessage ? [initialMessage] : [],
        );

        setView(CONTACT_WIDGET_VIEWS.CHAT);

        await registerPopupAction("chat_opened");
      } catch (error) {
        setErrorMessage(
          getErrorMessage(
            error,
            "Impossible d'envoyer votre question.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [ensureSessionToken, registerPopupAction],
  );

  const sendMessage = useCallback(
    async (message) => {
      const cleanMessage = message?.trim();

      if (!cleanMessage || !conversationId) {
        return false;
      }

      try {
        setSubmitting(true);
        setErrorMessage("");

        const sessionToken =
          await ensureSessionToken();

        const response = await sendVisitorMessage(
          conversationId,
          {
            session_token: sessionToken,
            message: cleanMessage,
          },
        );

        const newMessage = response?.data;

        if (newMessage) {
          setMessages((current) => [
            ...current,
            newMessage,
          ]);
        }

        await loadConversation({
          silent: true,
        });

        return true;
      } catch (error) {
        setErrorMessage(
          getErrorMessage(
            error,
            "Impossible d'envoyer le message.",
          ),
        );

        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [
      conversationId,
      ensureSessionToken,
      loadConversation,
    ],
  );

  const submitCallbackRequest = useCallback(
    async (formData) => {
      try {
        setSubmitting(true);
        setErrorMessage("");

        const sessionToken =
          await ensureSessionToken();

        saveVisitorContact(formData);

        const response =
          await createVisitorCallbackRequest({
            session_token: sessionToken,

            visitor_conversation_id:
              conversationId || null,

            name: formData.name,

            organization_name:
              formData.organization_name || null,

            email: formData.email || null,
            phone: formData.phone,

            preferred_date:
              formData.preferred_date || null,

            preferred_time:
              formData.preferred_time || null,

            product_interest:
              formData.product_interest || null,

            message: formData.message || null,
          });

        setSuccessData({
          type: "callback",
          title: "Votre demande est enregistrée",
          message:
            "Je vous recontacterai selon le créneau indiqué.",
          request: response?.data || null,
        });

        setView(CONTACT_WIDGET_VIEWS.SUCCESS);

        await registerPopupAction(
          "callback_opened",
        );
      } catch (error) {
        setErrorMessage(
          getErrorMessage(
            error,
            "Impossible d'enregistrer votre demande.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      conversationId,
      ensureSessionToken,
      registerPopupAction,
    ],
  );

  const submitDemoRequest = useCallback(
    async (formData) => {
      try {
        setSubmitting(true);
        setErrorMessage("");

        const sessionToken =
          await ensureSessionToken();

        saveVisitorContact(formData);

        const demoDetails = [
          formData.discipline
            ? `Discipline : ${formData.discipline}`
            : null,

          formData.members_count
            ? `Nombre d'adhérents : ${formData.members_count}`
            : null,

          formData.message
            ? `Message : ${formData.message}`
            : null,
        ]
          .filter(Boolean)
          .join("\n");

        const response =
          await createVisitorCallbackRequest({
            session_token: sessionToken,

            visitor_conversation_id:
              conversationId || null,

            name: formData.name,

            organization_name:
              formData.organization_name || null,

            email: formData.email || null,
            phone: formData.phone,

            preferred_date:
              formData.preferred_date || null,

            preferred_time:
              formData.preferred_time || null,

            product_interest:
              formData.product_interest ||
              "Démonstration MonAppliClub",

            message:
              demoDetails ||
              "Demande de démonstration.",
          });

        setSuccessData({
          type: "demo",
          title: "Demande de démonstration envoyée",
          message:
            "Je prendrai contact avec vous pour préparer une présentation adaptée à votre structure.",
          request: response?.data || null,
        });

        setView(CONTACT_WIDGET_VIEWS.SUCCESS);

        await registerPopupAction("clicked");
      } catch (error) {
        setErrorMessage(
          getErrorMessage(
            error,
            "Impossible d'envoyer la demande de démonstration.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      conversationId,
      ensureSessionToken,
      registerPopupAction,
    ],
  );

  /**
   * Invitation automatique après 45 secondes.
   */
  useEffect(() => {
    if (
      isOpen ||
      conversationId ||
      hasDismissedInvitation()
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setInvitationVisible(true);
      registerPopupAction("displayed");
    }, INVITATION_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    conversationId,
    isOpen,
    registerPopupAction,
  ]);

  /**
   * Chargement initial de la conversation existante.
   */
  useEffect(() => {
    if (!conversationId) return;

    loadConversation();
  }, [conversationId, loadConversation]);

  /**
   * Polling pour recevoir les réponses administrateur.
   */
  useEffect(() => {
    if (!conversationId) return undefined;

    const intervalId = window.setInterval(() => {
      loadConversation({
        silent: true,
      });
    }, CONVERSATION_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [conversationId, loadConversation]);

  /**
   * Actualisation immédiate après retour sur l'onglet.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        conversationId
      ) {
        loadConversation({
          silent: true,
        });
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [conversationId, loadConversation]);

  const unreadCount = Number(
    conversation?.unread_visitor_count || 0,
  );

  return {
    isOpen,
    view,

    conversationId,
    conversation,
    messages,

    unreadCount,

    invitationVisible,
    loadingConversation,
    submitting,

    errorMessage,
    successData,

    storedContact,
    suggestedProduct,

    setView,

    openWidget,
    closeWidget,
    goHome,
    dismissInvitation,

    loadConversation,

    startQuestionConversation,
    sendMessage,
    submitCallbackRequest,
    submitDemoRequest,
  };
}